import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { motion } from "framer-motion";
import { animations } from "../lib/animations";

const sizes = {
  xs: "sxs:max-w-xs",
  sm: "xs:max-w-sm",
  md: "xs:max-w-md",
  lg: "xs:max-w-lg",
  xl: "xs:max-w-xl",
  "2xl": "xs:max-w-2xl",
  "3xl": "xs:max-w-3xl",
  "4xl": "xs:max-w-4xl",
  "5xl": "xs:max-w-5xl",
};

export function Dialog({
  size = "lg",
  className,
  children,
  safeInsetBottom = 0,
  ...props
}: {
  size?: keyof typeof sizes;
  className?: string;
  children: React.ReactNode;
  safeInsetBottom?: number;
} & Omit<Headless.DialogProps, "as" | "className">) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-[--color-modal-backdrop] opacity-80 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in xs:px-6 xs:py-8 lg:px-8 lg:py-16"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 xs:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center xs:grid-rows-[1fr_auto_3fr] xs:p-4">
          <Headless.DialogPanel
            transition
            style={{
              borderWidth: "var(--modal-border-width)",
              borderColor: "var(--color-modal-border)",
              background:
                "var(--color-modal-background-linear-gradient, var(--color-modal-background))",
              paddingBottom: `${safeInsetBottom}px`,
            }}
            className={clsx(
              className,
              sizes[size],
              "row-start-2 w-full min-w-0 rounded-t-[--modal-border-radius] shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.05)] xs:shadow-lg xs:mb-auto xs:rounded-[--modal-border-radius] forced-colors:outline",
              "transition duration-100 will-change-transform data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in xs:data-[closed]:translate-y-0 xs:data-[closed]:data-[enter]:scale-95",
            )}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={animations.spring.bouncy}
            >
              {children}
            </motion.div>
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}
